/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { ICommerceAssistantData } from './commerce-assistant.data';
import { ICommerceAssistantProps, IHeadingData } from './commerce-assistant.props.autogenerated';
import { assistantStyles } from './commerce-assistant.styles';
import { isSecureEndpoint, sendAssistantMessageAsync } from './services/apim-assistant-client';

export type AssistantRole = 'user' | 'assistant';

/** Capturing split yields alternating plain/bold segments. */
const BOLD_SEGMENT_PATTERN = /\*\*(.+?)\*\*/;

/**
 * Renders the assistant's lightweight markdown (bold plus line breaks) as React nodes.
 * Model output is never injected as HTML, so it cannot introduce script.
 * @param text - Raw assistant reply.
 * @returns The reply as React elements.
 */
const renderAssistantText = (text: string): React.ReactNode => {
    return text.split('\n').map((line, lineIndex) => {
        const segments = line.split(BOLD_SEGMENT_PATTERN);
        const content = segments.map((segment, segmentIndex) =>
            segmentIndex % 2 === 1 ? <strong key={segmentIndex}>{segment}</strong> : segment
        );

        return (
            <div key={lineIndex} className='ms-commerce-assistant__line'>
                {line.length > 0 ? content : '\u00a0'}
            </div>
        );
    });
};

export interface IAssistantTranscriptEntry {
    id: number;
    role: AssistantRole;
    text: string;
}

export interface ICommerceAssistantViewProps extends ICommerceAssistantProps<ICommerceAssistantData> {
    moduleProps: IModuleProps;
    isOpen: boolean;
    heading?: React.ReactNode;
    launcher: React.ReactNode;
    panelTitle: React.ReactNode;
    closeButton: React.ReactNode;
    transcript: React.ReactNode;
    composer: React.ReactNode;
    alert?: React.ReactNode;
}

export interface ICommerceAssistantState {
    isOpen: boolean;
    inputValue: string;
    isSending: boolean;
    entries: IAssistantTranscriptEntry[];
    errorMessage?: string;
}

/**
 * CommerceAssistant component. Relays shopper prompts to the API Management assistant gateway.
 * @extends {React.Component<ICommerceAssistantProps<ICommerceAssistantData>, ICommerceAssistantState>}
 */
class CommerceAssistant extends React.Component<ICommerceAssistantProps<ICommerceAssistantData>, ICommerceAssistantState> {
    private readonly _className: string = 'ms-commerce-assistant';

    private readonly _transcriptRef: React.RefObject<HTMLDivElement>;

    private _previousResponseId?: string;

    private _nextEntryId: number = 0;

    public constructor(props: ICommerceAssistantProps<ICommerceAssistantData>) {
        super(props);

        this._transcriptRef = React.createRef();
        this.state = {
            isOpen: props.config.openByDefault === true,
            inputValue: '',
            isSending: false,
            entries: [{ id: this._nextEntryId++, role: 'assistant', text: props.resources.greetingText }]
        };
    }

    public componentDidUpdate(_prevProps: ICommerceAssistantProps<ICommerceAssistantData>, prevState: ICommerceAssistantState): void {
        if (prevState.entries !== this.state.entries && this._transcriptRef.current) {
            this._transcriptRef.current.scrollTop = this._transcriptRef.current.scrollHeight;
        }
    }

    public render(): JSX.Element | null {
        const { config, resources } = this.props;

        if (config.requireSignIn === true && !this._isSignedIn()) {
            return null;
        }

        const viewProps: ICommerceAssistantViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: classnames(this._className, config.className)
            },
            isOpen: this.state.isOpen,
            heading: this._renderHeading(config.heading),
            launcher: this._renderLauncher(),
            panelTitle: (
                <h2 className={`${this._className}__panel-title`} style={assistantStyles.panelTitle}>
                    {resources.panelTitle}
                </h2>
            ),
            closeButton: this._renderCloseButton(),
            transcript: this._renderTranscript(),
            composer: this._renderComposer(),
            alert: this._renderAlert()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _isSignedIn(): boolean {
        return this.props.context.request.user?.isAuthenticated === true;
    }

    private _getAuthorizationToken(): string | undefined {
        if (this.props.config.sendCustomerToken === false) {
            return undefined;
        }

        return this.props.context.request.user?.token ?? undefined;
    }

    private _renderHeading(heading?: IHeadingData): React.ReactNode | null {
        if (!heading?.text) {
            return null;
        }

        return <Heading {...heading} className={`${this._className}__heading`} />;
    }

    private _renderLauncher(): React.ReactNode {
        const { resources } = this.props;

        return (
            <button
                type='button'
                className={`${this._className}__launcher`}
                style={assistantStyles.launcher}
                aria-label={resources.launcherAriaLabel}
                title={resources.launcherText}
                aria-haspopup='dialog'
                aria-expanded={this.state.isOpen}
                onClick={this._onToggle}
            >
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' aria-hidden='true' focusable='false'>
                    <path
                        d='M12 3C6.99 3 3 6.58 3 11c0 2.28 1.06 4.33 2.76 5.78L5 20.5l3.94-1.86c.96.24 1.99.36 3.06.36 5.01 0 9-3.58 9-8s-3.99-8-9-8Z'
                        fill='currentColor'
                    />
                </svg>
            </button>
        );
    }

    private _renderCloseButton(): React.ReactNode {
        return (
            <button
                type='button'
                className={`${this._className}__close`}
                style={assistantStyles.closeButton}
                aria-label={this.props.resources.closeButtonAriaLabel}
                onClick={this._onToggle}
            >
                &#10005;
            </button>
        );
    }

    private _renderTranscript(): React.ReactNode {
        const { resources } = this.props;

        return (
            <div
                className={`${this._className}__transcript`}
                style={assistantStyles.transcript}
                ref={this._transcriptRef}
                role='log'
                aria-live='polite'
                aria-label={resources.transcriptAriaLabel}
            >
                {this.state.entries.map(entry => (
                    <div
                        key={entry.id}
                        className={`${this._className}__message ${this._className}__message--${entry.role}`}
                        style={entry.role === 'user' ? assistantStyles.messageUser : assistantStyles.messageAssistant}
                    >
                        {entry.role === 'assistant' ? renderAssistantText(entry.text) : entry.text}
                    </div>
                ))}
                {this.state.isSending ? (
                    <div className={`${this._className}__status`} style={assistantStyles.status}>
                        {resources.thinkingText}
                    </div>
                ) : null}
            </div>
        );
    }

    private _renderComposer(): React.ReactNode {
        const { resources } = this.props;
        const isDisabled = this.state.isSending || !isSecureEndpoint(this.props.config.apimChatEndpoint);

        return (
            <div className={`${this._className}__composer`} style={assistantStyles.composer}>
                <input
                    type='text'
                    className={`${this._className}__input`}
                    style={assistantStyles.input}
                    value={this.state.inputValue}
                    placeholder={resources.inputPlaceholder}
                    aria-label={resources.inputAriaLabel}
                    disabled={isDisabled}
                    onChange={this._onInputChange}
                    onKeyDown={this._onInputKeyDown}
                />
                <button
                    type='button'
                    className={`${this._className}__send`}
                    style={assistantStyles.sendButton}
                    aria-label={resources.sendButtonAriaLabel}
                    disabled={isDisabled || this.state.inputValue.trim().length === 0}
                    onClick={this._onSend}
                >
                    {resources.sendButtonText}
                </button>
            </div>
        );
    }

    private _renderAlert(): React.ReactNode | null {
        const { config, resources } = this.props;
        const message = isSecureEndpoint(config.apimChatEndpoint) ? this.state.errorMessage : resources.notConfiguredText;

        if (!message) {
            return null;
        }

        return (
            <div className={`${this._className}__alert`} style={assistantStyles.alert} role='alert'>
                {message}
            </div>
        );
    }

    private readonly _onToggle = (): void => {
        this.setState(previous => ({ isOpen: !previous.isOpen, errorMessage: undefined }));
    };

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ inputValue: event.target.value });
    };

    private readonly _onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            this._onSend();
        }
    };

    private readonly _onSend = (): void => {
        const message = this.state.inputValue.trim();

        if (message.length === 0 || this.state.isSending) {
            return;
        }

        this.setState(previous => ({
            entries: [...previous.entries, { id: this._nextEntryId++, role: 'user' as AssistantRole, text: message }],
            inputValue: '',
            isSending: true,
            errorMessage: undefined
        }));

        this._sendToApimAsync(message).catch((): void => undefined);
    };

    private async _sendToApimAsync(message: string): Promise<void> {
        const { config, resources } = this.props;

        try {
            const reply = await sendAssistantMessageAsync(
                {
                    endpointUrl: config.apimChatEndpoint,
                    authorizationToken: this._getAuthorizationToken()
                },
                message,
                this._previousResponseId
            );

            this._previousResponseId = reply.responseId ?? this._previousResponseId;

            this.setState(previous => ({
                entries: [
                    ...previous.entries,
                    { id: this._nextEntryId++, role: 'assistant' as AssistantRole, text: reply.text || resources.genericErrorText }
                ],
                isSending: false
            }));
        } catch (error) {
            this.props.telemetry.error(`Commerce assistant gateway call failed: ${(error as Error).message}`);
            this.setState({ isSending: false, errorMessage: resources.genericErrorText });
        }
    }
}

export default CommerceAssistant;
